// İSG Portal — SelectMenu (S33 planı §2.5, YENİ export — `Select` (yerel `<select>`) SESSİZCE
// DEĞİŞTİRİLMEZ: DOM'u ve klavye davranışını değiştiren bir Radix göçü web-supplier/kiosk için
// bir DAVRANIŞ değişikliği olurdu, oysa plan bunu ayrı bir export olarak İSTİYOR — plan §2.5
// "changes DOM structure and keyboard behavior ... New export SelectMenu". `Select` her iki
// derlemede de AYNI kalır, bu iki export PARALEL yaşar. İlk gerçek tüketici Slice 3'ün
// "Select reason" (Reject) diyaloğu + Slice 4'ün tablo filtreleri (plan §2.5).
//
// Kanonik seçenek şekli `Select.tsx`'i AYNALAR (`{ value, label }[]`,
// [[feedback_bibulut_selectlist_canonical_shape]]).

import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { SelectOption } from './Select';
import { cn } from './cn';

export interface SelectMenuProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Tetikleyicinin erişilebilir adı — bir `Field`e bağlı DEĞİLSE zorunlu (i18n ile). */
  ariaLabel?: string;
  /** Tetikleyicinin `id`'si (S33-8 review fix #7) — bir `Field`in `htmlFor`ına native
   * `<label for>` bağlaması için; verilirse `ariaLabel` genelde GEREKSİZDİR (bağlı `<label>`
   * zaten erişilebilir adı sağlar — ikisi birden verilirse `aria-label` kazanır, ARIA'nın
   * "aria-label bağlı label'ı ezer" kuralı). */
  id?: string;
  className?: string;
}

export function SelectMenu({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  invalid,
  ariaLabel,
  id,
  className,
}: SelectMenuProps) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-surface px-3 text-sm text-fg',
          'transition-colors data-[placeholder]:text-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'carbon:rounded-none carbon:border-0 carbon:border-b carbon:px-4 carbon:focus-visible:ring-inset',
          'carbon:data-[placeholder]:text-placeholder',
          // `Input.tsx`'in dosya başlığı notunu AYNALAR (S33 Slice 6 global düzeltme #2) —
          // yalnız tetikleyici (trigger), açılan liste (`SelectPrimitive.Content`) DEĞİL: liste
          // zaten kendi yükseltilmiş yüzeyi (`bg-surface` + `shadow-md`), form kontrolü değil.
          'carbon:[[data-tile]_&]:bg-field',
          invalid
            ? 'border-danger focus-visible:ring-danger carbon:border-b-danger'
            : 'border-border-strong carbon:border-b-border-strong',
          className,
        )}
      >
        {/* `truncate` + `title` (S33 Slice 4 QA fix, MEDIUM — defekt #3; S34-3 design review
            QA follow-up S6): uzun DE etiketleri ("Alle Genehmigungsarten") 40px tetikleyicide 2
            satıra sarıyordu. `className` doğrudan `SelectPrimitive.Value`'ya VERİLEMEZ — Radix
            kaynağında (`react-select` dist, `SelectValue`) `className`/`style` prop'u
            DESTRUCTURE edilip hiç `Primitive.span`'a geri SPREAD EDİLMİYOR (yalnız `title` gibi
            tanınmayan prop'lar `...valueProps` ile geçer); verilen sınıflar DOM'a hiç ULAŞMIYOR
            (QA'nın `className=""` gözlemi bunu doğruladı). Bu yüzden sınıflar KENDİMİZİN sardığı
            bir `<span>`'e taşındı — `flex-1 min-w-0` bu satırın flex tetikleyicide gerçekten
            küçülüp kalan genişliği alması İÇİN GEREKLİ, `truncate` (nowrap+ellipsis) taşan
            metni keser. `title` hâlâ `SelectPrimitive.Value`'da KALIYOR (o zaten doğru geçiyordu,
            QA'nın "title does" gözlemi). */}
        <span className="min-w-0 flex-1 truncate text-left">
          <SelectPrimitive.Value
            placeholder={placeholder}
            title={
              value ? (options.find((opt) => opt.value === value)?.label ?? undefined) : undefined
            }
          />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-[var(--z-dropdown)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-border bg-surface p-1 shadow-md',
            'carbon:rounded-none carbon:border-0 carbon:p-0',
          )}
        >
          <SelectPrimitive.Viewport>
            {options.map((opt) => (
              <SelectPrimitive.Item
                key={opt.value}
                value={opt.value}
                className={cn(
                  'relative flex h-9 cursor-pointer select-none items-center rounded-sm px-3 pr-8 text-sm text-fg outline-none',
                  'data-[highlighted]:bg-surface-muted data-[state=checked]:font-medium',
                  'carbon:h-10 carbon:rounded-none carbon:px-4 carbon:data-[highlighted]:bg-surface-hover',
                )}
              >
                <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-3 inline-flex items-center">
                  <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
